// API Configuration Types
export interface FingerprintConfig {
  /**
   * API endpoint for fingerprint collection
   * @default "https://api.fingerprint.techstudio.live"
   */
  apiEndpoint?: string;

  /**
   * Your public API key for the fingerprint service
   */
  publicApiKey?: string;

  /**
   * Custom headers to include with requests
   */
  headers?: Record<string, string>;

  /**
   * Timeout for API requests in milliseconds
   * @default 10000
   */
  timeout?: number;

  /**
   * Whether to include debug information in the response
   * @default false
   */
  debug?: boolean;

  /**
   * If true, allow sending payloads as plaintext when `crypto.subtle` is
   * unavailable (HTTP origins, very old browsers, etc.). The server will only
   * accept plaintext from API keys with `environment = 'test'`.
   *
   * Default is `false` — the SDK fails closed and returns a degraded result
   * rather than silently downgrading. Setting this to `true` is intended for
   * development against an HTTP staging host with a test API key.
   *
   * @default false
   */
  allowPlaintext?: boolean;

  /**
   * Optional callback invoked when fingerprint collection or transport fails.
   * `fp.get()` always resolves with a degraded result on failure (visitorId
   * empty, flags `unlikely`, scores `0`); use this hook to log errors to your
   * own telemetry. Throwing inside this callback is swallowed.
   *
   * Receives the original FingerprintError so callers can branch on
   * `err.code` (e.g. CRYPTO_UNAVAILABLE, NETWORK_ERROR, TIMEOUT,
   * COLLECTION_ERROR, INVALID_RESPONSE).
   */
  onError?: (error: FingerprintError) => void;
}

// Fingerprint Result Types
export interface FingerprintResult {
  /**
   * Unique visitor identifier that persists across sessions
   */
  visitorId: string;

  /**
   * Device-specific core identifier (stable across browser modes)
   */
  deviceCoreId: string;

  /**
   * Browser-scoped identifier (changes between browsers)
   */
  browserScopedId: string;

  /**
   * Unique identifier for this observation/request
   */
  observationId: string;

  /**
   * Client IP address
   */
  ip: string | null;

  /**
   * Detection flags for special conditions
   */
  flags: {
    /**
     * VPN/Proxy usage detection
     */
    isVpn: "likely" | "possible" | "unlikely";

    /**
     * Incognito/Private mode detection
     */
    isIncognito: "likely" | "possible" | "unlikely";

    /**
     * Bot / automation (headless, webdriver, etc.) — heuristic
     */
    isBot: "likely" | "possible" | "unlikely";
  };

  /**
   * Confidence scores (0-1) for detection flags
   */
  scores: {
    vpn: number;
    incognito: number;
    bot: number;
  };

  /**
   * Timestamp when the fingerprint was collected
   */
  timestamp: string;

  /**
   * Server core-identity algorithm version (e.g. v4)
   */
  algoVersion?: string;

  /**
   * Tiered visitor linker result (vector + hard signals + optional continuity)
   */
  visitorLink?: {
    tier: "strong" | "medium" | "none";
    similarity?: number | null;
    margin?: number | null;
    method?: string;
    linked_to_device_core_id?: string;
    same_ip?: boolean;
    possible_prior_visitor_id?: string;
    note?: string;
  };

  /**
   * Raw fingerprint data (if debug mode is enabled)
   */
  fingerprint?: Record<string, any>;

  /**
   * Additional debug information (if debug mode is enabled)
   */
  verification?: Record<string, any>;

  /**
   * Visitor linking information (if debug mode is enabled)
   */
  visitor_linking?: Record<string, any>;

  /**
   * Optional first-party continuity token (HMAC) when server is configured with CONTINUITY_HMAC_SECRET.
   * Not part of device_core_id; store client-side and send back as continuity_hint on the next collect.
   */
  continuity?: {
    visitor_id: string;
    issued_at: number;
    sig: string;
  };
}

// Browser Feature Types (for internal use)
export interface BrowserFeatures {
  browser?: {
    isBrave?: boolean;
    isSafari?: boolean;
    isFirefox?: boolean;
    /** Chrome on iOS (CriOS) — WebKit, not Blink */
    isIosChrome?: boolean;
    ua?: string;
    brands?: string[];
    family?: string;
    version?: string;
    engine?: string;
  };
  uaCh?: {
    brands?: any[];
    platform?: string;
    platformVersion?: string;
    model?: string;
    mobile?: boolean;
    fullVersionList?: any;
  };
  browserFamily?: string;
  screen?: {
    primaryW?: number | null;
    primaryH?: number | null;
    dpr?: number | null;
    depth?: number | null;
    source?: string;
    maxTexture?: number;
    gpuRenderer?: string;
    gpuVendor?: string;
    displayCount?: number;
  };
  screenRaw?: {
    w?: number;
    h?: number;
    dpr?: number;
    colorDepth?: number;
  };
  display?: {
    gamut?: string;
    hdr?: boolean;
  };
  hw?: {
    mem?: number | null;
    cores?: number | null;
    touch?: boolean;
  };
  gl?: {
    vendor?: string;
    renderer?: string;
    glHash?: string;
  };
  canvas?: {
    hash?: string;
  };
  audio?: {
    hash?: string;
    sampleRate?: number;
  };
  fonts?: string[];
  storage?: {
    idb?: boolean;
    ls?: boolean;
    quotaMB?: number;
    firefoxPrivateIndicators?: { score: number; signals: string[] };
    bravePrivateIndicators?: { score: number; signals: string[] };
    chromiumPrivateIndicators?: { score: number; signals: string[] };
    timerPrecision?: number;
    opfsFlushPrivate?: boolean;
    safariOpfsPrivate?: boolean;
  };
  tz?: string;
  locales?: readonly string[];
  network?: {
    connection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      saveData?: boolean;
    };
    ipv6Support?: boolean;
    ipv4Support?: boolean;
    dualStack?: boolean;
    connectionTest?: {
      ipv6Latency?: number;
      ipv4Latency?: number;
      ipv6Success?: boolean;
      ipv4Success?: boolean;
    };
    timingAnalysis?: {
      rttJitter?: number;
      vpnIndicatorScore?: number;
      incognitoIndicatorScore?: number;
    };
  };
  incognitoContext?: {
    isTopLevel?: boolean;
    platform?: string;
    uaPlatform?: string;
    chromiumMajorVersion?: number | null;
    isIosChrome?: boolean;
  };
  visit?: {
    domain?: string;
    url?: string;
    referrer?: string;
  };
  /** Bot / automation detection signals (not used in device_core_id) */
  bot?: {
    webdriver?: boolean;
    outerSizeZero?: boolean;
    automationProps?: string[];
    pluginCount?: number;
    chromeRuntime?: boolean | null;
    languagesEmpty?: boolean;
    screenZero?: boolean;
    hasFocus?: boolean | null;
  };
}

// SDK Instance Interface
export interface FingerprintSDK {
  /**
   * Get the current fingerprint result
   * @returns Promise resolving to fingerprint data
   */
  get(): Promise<FingerprintResult>;

  /**
   * Get the current fingerprint result with additional options
   * @param options Additional options for this request
   * @returns Promise resolving to fingerprint data
   */
  get(options?: { timeout?: number; debug?: boolean }): Promise<FingerprintResult>;
}

// Load Function Type
export interface LoadFunction {
  /**
   * Initialize the fingerprint SDK with configuration
   * @param config Configuration options
   * @returns Promise resolving to SDK instance
   */
  (config?: FingerprintConfig): Promise<FingerprintSDK>;
}

// Error Types
export class FingerprintError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode?: number,
  ) {
    super(message);
    this.name = "FingerprintError";
  }
}

export class FingerprintTimeoutError extends FingerprintError {
  constructor(timeout: number) {
    super(`Request timed out after ${timeout}ms`, "TIMEOUT");
  }
}

export class FingerprintNetworkError extends FingerprintError {
  constructor(message: string, statusCode?: number) {
    super(message, "NETWORK_ERROR", statusCode);
  }
}
